/** docs/UX.md 13 · `/m/speakers` 说话人 */
import { useMemo, useState } from "react";
import { Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { SpeakerImportPayload, SpeakerUpdatePayload } from "@xinghai/shared";
import {
  Button,
  EmptyState,
  Field,
  Icon,
  Input,
  Select,
  Sheet,
  Table,
  useToast,
} from "@xinghai/ui";
import type { Column, IconId } from "@xinghai/ui";
import { speakersApi } from "../../lib/api";
import { qk } from "../../lib/queries";
import { CheckboxPicker, KeyValue, PageHeader, QueryBoundary, StatusTag } from "./common";
import { DIALECT_GROUPS, TRAINING_LEVELS } from "./labels";
import { IssueList } from "./MaterialsPage";
import { parseJsonInput, readTextFile, validateSpeakerImport } from "./importValidation";
import type { ImportIssue } from "./importValidation";
import type { SpeakerListItem } from "./types";
import { ViewAsStudentButton } from "./ViewAsStudentButton";

type SpeakerTab = "all" | "unverified";

const CONSENT_ITEMS: Array<{ key: "train" | "display" | "commercial"; label: string }> = [
  { key: "train", label: "训练" },
  { key: "display", label: "展示" },
  { key: "commercial", label: "商用" },
];

/** 三项授权 tri-state: 已签 / 未签 / 已撤回 — icon + text, never colour alone. */
function ConsentIcons({ s }: { s: SpeakerListItem }) {
  const withdrawn = !!s.consent_withdrawn_at;
  return (
    <span className="xz-adm__tags">
      {CONSENT_ITEMS.map((c) => {
        const signed = s.consent[c.key];
        const icon: IconId = withdrawn ? "ui-ban" : signed ? "ui-check-circle" : "ui-close";
        const tone = withdrawn ? "danger" : signed ? "success" : "warning";
        const state = withdrawn ? "已撤回" : signed ? "已签" : "未签";
        return (
          <StatusTag key={c.key} icon={icon} tone={tone}>
            {c.label}
            {state}
          </StatusTag>
        );
      })}
    </span>
  );
}

export function SpeakersPage() {
  const [query, setQuery] = useState("");
  const [classFilter, setClassFilter] = useState("");
  const [editing, setEditing] = useState<SpeakerListItem | null>(null);
  const [verifying, setVerifying] = useState<SpeakerListItem | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [tab, setTab] = useState<SpeakerTab>("all");

  const list = useQuery({
    queryKey: qk.speakersFiltered(tab === "unverified" ? { verified: 0 } : {}),
    queryFn: ({ signal }) => speakersApi.list(tab === "unverified" ? { verified: 0 } : {}, signal),
  });

  const classes = useMemo(() => {
    const set = new Set<string>();
    for (const s of list.data?.speakers ?? []) if (s.class_id) set.add(s.class_id);
    return [...set].sort();
  }, [list.data]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (list.data?.speakers ?? []).filter((s) => {
      // Belt and braces: keep the tab honest even if the server ignores `verified`.
      if (tab === "unverified" && s.verified === true) return false;
      if (classFilter && s.class_id !== classFilter) return false;
      if (!q) return true;
      return (
        s.id.toLowerCase().includes(q) ||
        (s.display_name ?? "").toLowerCase().includes(q) ||
        (s.username ?? "").toLowerCase().includes(q) ||
        s.dialect_l1.some((d) => d.toLowerCase().includes(q))
      );
    });
  }, [list.data, query, classFilter, tab]);

  const unverifiedCount = useMemo(
    () => (list.data?.speakers ?? []).filter((s) => s.verified === false).length,
    [list.data],
  );

  const columns: Array<Column<SpeakerListItem>> = [
    {
      key: "id",
      header: "说话人",
      render: (s) => <span className="xz-adm__mono">{s.id}</span>,
      width: "9rem",
    },
    {
      key: "name",
      header: "姓名",
      render: (s) => (
        <>
          {s.display_name ?? "—"}
          {s.username && <span className="gz-caption"> @{s.username}</span>}
        </>
      ),
    },
    {
      key: "dialect",
      header: "方言背景",
      render: (s) =>
        s.dialect_l1.length === 0 ? (
          <span className="gz-muted">未填</span>
        ) : (
          <span className="xz-adm__tags">
            {s.dialect_l1.map((d) => (
              <StatusTag key={d} icon="ui-language">
                {d}
              </StatusTag>
            ))}
          </span>
        ),
    },
    {
      key: "level",
      header: "水平",
      render: (s) =>
        TRAINING_LEVELS.find((l) => l.value === s.training_level)?.label ?? s.training_level ?? "—",
    },
    { key: "class", header: "班级", render: (s) => s.class_id ?? "—" },
    {
      key: "verified",
      header: "核验",
      render: (s) =>
        s.verified === undefined ? (
          <span className="gz-muted">—</span>
        ) : s.verified ? (
          <StatusTag icon="ui-check-circle" tone="success">
            已核验
          </StatusTag>
        ) : (
          <StatusTag icon="ui-clock" tone="warning">
            待核验
          </StatusTag>
        ),
    },
    { key: "consent", header: "三项授权", render: (s) => <ConsentIcons s={s} /> },
    {
      key: "minor",
      header: "未成年",
      render: (s) =>
        s.is_minor ? (
          <StatusTag icon="ui-shield" tone={s.minor_guardian_signed ? "success" : "warning"}>
            {s.minor_guardian_signed ? "监护人已联签" : "待监护人联签"}
          </StatusTag>
        ) : (
          <span className="gz-muted">否</span>
        ),
    },
    {
      key: "actions",
      header: "操作",
      render: (s) => (
        <span className="xz-adm__rowactions">
          {s.verified === false && (
            <Button size="sm" onClick={() => setVerifying(s)}>
              核验
            </Button>
          )}
          <Button size="sm" variant="secondary" onClick={() => setEditing(s)}>
            编辑
          </Button>
          <Link className="gz-button xz-button--sm" to={`/m/speakers/${s.id}/recordings`}>
            回看
          </Link>
          <ViewAsStudentButton userId={s.user_id} />
        </span>
      ),
    },
  ];

  return (
    <div className="xz-adm">
      <PageHeader
        title="说话人"
        description="方言背景、水平、班级与三项授权状态。授权未全签的说话人不会被派发任务（红线 14.6）。"
        actions={
          <Button
            leadingIcon={<Icon id="ui-upload" size={16} />}
            onClick={() => setImportOpen(true)}
          >
            导入名单
          </Button>
        }
      />

      <div className="xz-tabs" role="tablist" aria-label="说话人筛选">
        <button
          type="button"
          role="tab"
          className="xz-tabs__tab"
          aria-selected={tab === "all"}
          onClick={() => setTab("all")}
        >
          <Icon id="ui-users" size={16} /> 全部
        </button>
        <button
          type="button"
          role="tab"
          className="xz-tabs__tab"
          aria-selected={tab === "unverified"}
          onClick={() => setTab("unverified")}
        >
          <Icon id="ui-clock" size={16} /> 待核验
          {tab === "all" && unverifiedCount > 0 ? ` ${unverifiedCount}` : ""}
        </button>
      </div>

      <div className="xz-adm__toolbar">
        <Field label="搜索">
          <Input
            type="search"
            value={query}
            placeholder="说话人编号 / 姓名 / 方言"
            onChange={(e) => setQuery(e.target.value)}
          />
        </Field>
        <Field label="班级">
          <Select
            value={classFilter}
            onChange={(e) => setClassFilter(e.target.value)}
            options={[
              { value: "", label: "全部班级" },
              ...classes.map((c) => ({ value: c, label: c })),
            ]}
          />
        </Field>
      </div>

      <QueryBoundary
        isPending={list.isPending}
        isError={list.isError}
        error={list.error}
        onRetry={() => void list.refetch()}
      >
        <>
          <p className="gz-caption">
            {tab === "unverified" ? `${rows.length} 位待核验` : `共 ${rows.length} 位说话人`}
          </p>
          <Table
            caption="说话人名单"
            columns={columns}
            rows={rows}
            rowKey={(s) => s.id}
            empty={
              tab === "unverified" ? (
                <EmptyState
                  title="没有待核验的学生"
                  description="自助注册的学生会先进到这里，等你核对方言与水平。"
                />
              ) : (
                <EmptyState title="没有符合条件的说话人" description="换个搜索词，或先导入名单。" />
              )
            }
          />
        </>
      </QueryBoundary>

      {verifying && (
        <VerifySheet speaker={verifying} classes={classes} onClose={() => setVerifying(null)} />
      )}
      {editing && (
        <SpeakerEditSheet speaker={editing} classes={classes} onClose={() => setEditing(null)} />
      )}
      <SpeakerImportSheet open={importOpen} onClose={() => setImportOpen(false)} />
    </div>
  );
}

// --------------------------------------------------------------------------- //
// 核验 (ACCOUNTS.md §6)
// --------------------------------------------------------------------------- //

function VerifySheet({
  speaker,
  classes,
  onClose,
}: {
  speaker: SpeakerListItem;
  classes: string[];
  onClose: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const [classId, setClassId] = useState(speaker.class_id ?? "");
  const [checked, setChecked] = useState(false);

  const verify = useMutation({
    mutationFn: () =>
      speakersApi.verify(
        speaker.id,
        classId !== (speaker.class_id ?? "") ? { class_id: classId || null } : {},
      ),
    onSuccess: () => {
      toast.success(`${speaker.id} 已核验`);
      void qc.invalidateQueries({ queryKey: ["speakers"] });
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Sheet
      open
      onClose={onClose}
      title={`核验说话人 ${speaker.id}`}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            稍后再说
          </Button>
          <Button disabled={!checked} loading={verify.isPending} onClick={() => verify.mutate()}>
            核验通过
          </Button>
        </>
      }
    >
      <div className="xz-form">
        <p className="gz-muted">
          这位学生是自助注册的。核对一下他填的背景资料，必要时改班级，再点「核验通过」。未核验不影响他录音，
          但导出审计会给 SPEAKER_UNVERIFIED 警告。
        </p>

        <KeyValue
          items={[
            { key: "name", label: "姓名", value: speaker.display_name ?? "—" },
            { key: "username", label: "用户名", value: speaker.username ?? "—" },
            {
              key: "dialect",
              label: "方言背景",
              value:
                speaker.dialect_l1.length === 0 ? (
                  <span className="gz-muted">未填</span>
                ) : (
                  <span className="xz-adm__tags">
                    {speaker.dialect_l1.map((d) => (
                      <StatusTag key={d} icon="ui-language">
                        {d}
                      </StatusTag>
                    ))}
                  </span>
                ),
            },
            { key: "hometown", label: "家乡", value: speaker.hometown ?? "—" },
            {
              key: "level",
              label: "训练水平",
              value:
                TRAINING_LEVELS.find((l) => l.value === speaker.training_level)?.label ??
                speaker.training_level ??
                "—",
            },
            {
              key: "minor",
              label: "未成年",
              value: speaker.is_minor
                ? speaker.minor_guardian_signed
                  ? "是 · 监护人已联签"
                  : "是 · 待监护人联签"
                : "否",
            },
            { key: "birth", label: "出生年份", value: speaker.birth_year ?? "—" },
            { key: "contact", label: "联系方式", value: speaker.contact ?? "—" },
          ]}
        />

        <div>
          <h3 className="gz-type-heading">三项授权</h3>
          <ConsentIcons s={speaker} />
        </div>

        <Field label="班级" hint="学生用哪个班的邀请码注册就落在哪个班；核对后可以改。">
          <Select
            value={classId}
            onChange={(e) => setClassId(e.target.value)}
            options={[
              { value: "", label: "未分班" },
              ...classes.map((c) => ({ value: c, label: c })),
            ]}
          />
        </Field>

        {speaker.is_minor && !speaker.minor_guardian_signed && (
          <div className="gz-notice" role="status">
            <Icon id="ui-warning" size={20} />
            <span>这位学生是未成年人且监护人尚未联签，导出审计会报 14.6，请先补齐联签。</span>
          </div>
        )}

        <label className="xz-form__choice">
          <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
          <span>
            <span>我已核对方言背景、训练水平与班级</span>
            <span className="gz-caption">核验动作会写入审计记录。</span>
          </span>
        </label>
      </div>
    </Sheet>
  );
}

// --------------------------------------------------------------------------- //
// edit
// --------------------------------------------------------------------------- //

function SpeakerEditSheet({
  speaker,
  classes,
  onClose,
}: {
  speaker: SpeakerListItem;
  classes: string[];
  onClose: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const [dialects, setDialects] = useState<string[]>(speaker.dialect_l1);
  const [level, setLevel] = useState(speaker.training_level ?? "");
  const [classId, setClassId] = useState(speaker.class_id ?? "");
  const [guardian, setGuardian] = useState(speaker.minor_guardian_signed);

  const save = useMutation({
    mutationFn: (patch: SpeakerUpdatePayload) => speakersApi.update(speaker.id, patch),
    onSuccess: () => {
      toast.success(`已保存 ${speaker.id}`);
      void qc.invalidateQueries({ queryKey: ["speakers"] });
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Sheet
      open
      onClose={onClose}
      title={`编辑说话人 ${speaker.id}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            取消
          </Button>
          <Button
            loading={save.isPending}
            onClick={() =>
              save.mutate({
                dialect_l1: dialects,
                training_level: level,
                class_id: classId || null,
                ...(speaker.is_minor ? { minor_guardian_signed: guardian } : {}),
              })
            }
          >
            保存
          </Button>
        </>
      }
    >
      <div className="xz-form">
        <CheckboxPicker
          label="母方言（可多选）"
          options={DIALECT_GROUPS.map((d) => ({ value: d, label: d }))}
          value={dialects}
          onChange={setDialects}
        />

        <div className="xz-form__row">
          <Field label="水平">
            <Select
              value={level}
              onChange={(e) => setLevel(e.target.value)}
              options={[{ value: "", label: "未填" }, ...TRAINING_LEVELS]}
            />
          </Field>
          <Field label="班级">
            <Select
              value={classId}
              onChange={(e) => setClassId(e.target.value)}
              options={[
                { value: "", label: "未分班" },
                ...classes.map((c) => ({ value: c, label: c })),
              ]}
            />
          </Field>
        </div>

        {speaker.is_minor && (
          <label className="xz-form__choice">
            <input
              type="checkbox"
              checked={guardian}
              onChange={(e) => setGuardian(e.target.checked)}
            />
            <span>
              <span>监护人已联签</span>
              <span className="gz-caption">
                未成年说话人必须有监护人联签，否则导出审计会报 14.6。
              </span>
            </span>
          </label>
        )}

        <div>
          <h3 className="gz-type-heading">三项授权</h3>
          <ConsentIcons s={speaker} />
          <p className="gz-caption">授权由学生本人在「知情同意」页签署或撤回，这里只做查看。</p>
        </div>
      </div>
    </Sheet>
  );
}

// --------------------------------------------------------------------------- //
// import
// --------------------------------------------------------------------------- //

function SpeakerImportSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [fileName, setFileName] = useState<string | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [issues, setIssues] = useState<ImportIssue[]>([]);
  const [payload, setPayload] = useState<SpeakerImportPayload | null>(null);
  const [count, setCount] = useState(0);
  const [created, setCreated] = useState<Array<{ username: string; temp_password?: string }>>([]);

  const reset = () => {
    setFileName(null);
    setParseError(null);
    setIssues([]);
    setPayload(null);
    setCount(0);
  };

  const run = useMutation({
    mutationFn: (p: SpeakerImportPayload) => speakersApi.import(p),
    onSuccess: (r) => {
      toast.success(`已导入 ${r.imported} 位说话人`);
      setCreated(r.speakers.filter((s) => s.temp_password));
      void qc.invalidateQueries({ queryKey: ["speakers"] });
      reset();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const onFile = async (file: File | null) => {
    reset();
    setCreated([]);
    if (!file) return;
    setFileName(file.name);
    let text: string;
    try {
      text = await readTextFile(file);
    } catch (err) {
      setParseError(`读取文件失败：${(err as Error).message}`);
      return;
    }
    const { value, error } = parseJsonInput(text);
    if (error) {
      setParseError(error);
      return;
    }
    const result = validateSpeakerImport(value);
    setIssues(result.issues);
    setPayload(result.payload);
    setCount(result.count);
  };

  const canImport = payload !== null && issues.length === 0 && parseError === null;

  return (
    <Sheet
      open={open}
      onClose={() => {
        reset();
        setCreated([]);
        onClose();
      }}
      title="导入说话人名单"
      size="lg"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => {
              reset();
              setCreated([]);
              onClose();
            }}
          >
            关闭
          </Button>
          <Button
            disabled={!canImport}
            loading={run.isPending}
            onClick={() => payload && run.mutate(payload)}
          >
            导入
          </Button>
        </>
      }
    >
      <div className="xz-form">
        <Field
          label="名单文件（JSON 数组）"
          hint="每行需要 username 与 speaker_code；dialect_l1 / training_level / class_id / is_minor 可选。"
        >
          <input
            type="file"
            accept="application/json,.json"
            aria-label="选择说话人 JSON 文件"
            onChange={(e) => void onFile(e.target.files?.[0] ?? null)}
          />
        </Field>

        {fileName && <p className="gz-caption">已选择：{fileName}</p>}

        {parseError && (
          <div className="gz-notice gz-notice--error" role="alert">
            <Icon id="ui-error" size={20} />
            <span>{parseError}</span>
          </div>
        )}

        {issues.length > 0 && (
          <div>
            <h3 className="gz-type-heading">{issues.length} 处问题，先改好才能导入</h3>
            <IssueList issues={issues} />
          </div>
        )}

        {canImport && (
          <div className="gz-notice gz-notice--success" role="status">
            <Icon id="ui-check-circle" size={20} />
            <span>校验通过，共 {count} 位说话人可以导入。</span>
          </div>
        )}

        {created.length > 0 && (
          <div>
            <h3 className="gz-type-heading">新建账号的初始密码（只显示这一次）</h3>
            <ul className="xz-issues">
              {created.map((c) => (
                <li key={c.username} className="xz-issues__item">
                  <span className="xz-issues__where">{c.username}</span>
                  <span className="xz-adm__mono">{c.temp_password}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Sheet>
  );
}
